import type { Visualizer } from './types';
import { clearBg, drawHud } from './types';

/**
 * Emoji stream — performance-first.
 * No shadowBlur (GPU/CPU killer), fixed pool, low particle cap.
 */

type EmojiP = {
  alive: boolean;
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  rot: number;
  spin: number;
  emoji: string;
  life: number;
  bin: number;
};

const POOL = [
  '🎵', '🎶', '🎧', '🎤', '🔥', '💜', '✨', '⚡', '💚', '💖',
  '⭐', '🚀', '👾', '💿', '🔊', '🥁', '😎', '💥', '🌸', '🫧',
];

const MAX = 36;
const particles: EmojiP[] = Array.from({ length: MAX }, () => ({
  alive: false,
  x: 0,
  y: 0,
  vx: 0,
  vy: 0,
  size: 20,
  rot: 0,
  spin: 0,
  emoji: POOL[0]!,
  life: 0,
  bin: 0,
}));

let spawnAcc = 0;
let aliveCount = 0;

function spawnOne(
  w: number,
  h: number,
  bands: { bass: number; energy: number; mids: number },
  i: number,
): void {
  const p = particles[i]!;
  if (p.alive) return;
  const fromLeft = Math.random() > 0.5;
  p.alive = true;
  p.x = fromLeft ? -30 : w + 30;
  p.y = h * (0.18 + Math.random() * 0.64);
  p.vx = (fromLeft ? 1 : -1) * (1.4 + Math.random() * 2.2 + bands.energy * 3.2);
  p.vy = (Math.random() - 0.5) * (0.8 + bands.mids * 1.5);
  p.size = 16 + Math.random() * 14 + bands.bass * 12;
  p.rot = Math.random() * Math.PI * 2;
  p.spin = (Math.random() - 0.5) * 0.08;
  p.emoji = POOL[(Math.random() * POOL.length) | 0]!;
  p.life = 1;
  p.bin = (Math.random() * 64) | 0;
  aliveCount++;
}

export const emojiStreamViz: Visualizer = {
  id: 'emoji-stream',
  label: 'Emoji Stream',
  draw(frame) {
    const { ctx, w, h, bands, freq, t } = frame;
    clearBg(ctx, w, h, 0.4);

    // soft center — one gradient only
    const g = ctx.createRadialGradient(w / 2, h / 2, 8, w / 2, h / 2, Math.min(w, h) * 0.45);
    g.addColorStop(0, `rgba(86,240,200,${0.03 + bands.energy * 0.06})`);
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);

    // target population scales with energy, hard-capped
    const target = Math.min(MAX, Math.floor(8 + bands.energy * 22 + bands.kick * 8));
    spawnAcc += 0.25 + bands.energy * 1.8 + bands.kick * 2.2 + bands.beat * 1.5;
    while (spawnAcc >= 1 && aliveCount < target) {
      // find free slot
      let slot = -1;
      for (let i = 0; i < MAX; i++) {
        if (!particles[i]!.alive) {
          slot = i;
          break;
        }
      }
      if (slot < 0) break;
      spawnOne(w, h, bands, slot);
      spawnAcc -= 1;
    }
    // drain excess spawn intent
    if (spawnAcc > 3) spawnAcc = 3;

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    for (let i = 0; i < MAX; i++) {
      const p = particles[i]!;
      if (!p.alive) continue;
      const f = freq[p.bin % freq.length]! / 255;
      p.x += p.vx * (1 + bands.bass * 0.5 + f * 0.4);
      p.y += p.vy + Math.sin(t * 2.5 + p.bin) * bands.mids * 1.2;
      p.rot += p.spin * (1 + bands.highs + bands.beat * 0.5);
      if (bands.beat > 0.4) {
        p.y += Math.sin(t * 14 + p.bin) * bands.beat * 2.5;
      }
      p.life -= 0.0035 + (1 - bands.energy) * 0.002;

      if (p.life <= 0 || p.x < -60 || p.x > w + 60 || p.y < -60 || p.y > h + 60) {
        p.alive = false;
        aliveCount = Math.max(0, aliveCount - 1);
        continue;
      }

      const scale = p.size * (0.9 + f * 0.35 + bands.kick * 0.15);
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.globalAlpha = Math.min(1, 0.5 + f * 0.45) * Math.max(0.12, p.life);
      // integer font sizes — cheaper
      ctx.font = `${Math.round(scale)}px "Segoe UI Emoji","Apple Color Emoji",sans-serif`;
      ctx.fillText(p.emoji, 0, 0);
      ctx.restore();
    }
    ctx.globalAlpha = 1;

    // cheap bottom sparkline — 24 dots only, no emoji
    ctx.strokeStyle = `rgba(86,240,200,${0.25 + bands.energy * 0.35})`;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    const step = Math.max(1, Math.floor(frame.wave.length / 48));
    for (let i = 0; i < frame.wave.length; i += step) {
      const x = (i / frame.wave.length) * w;
      const y = h * 0.9 + (frame.wave[i] || 0) * h * 0.08 * (1 + bands.energy);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();

    drawHud(frame);
  },
};
